import { useEffect } from 'react';

import LucideSymbolIcon from '../icons/LucideSymbolIcon';

export type ElearningActionMenuItem = {
  id: string;
  label: string;
  icon: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
};

type Props = {
  menuKey: string;
  openMenuKey: string | null;
  onOpenMenuKeyChange: (key: string | null) => void;
  items: ElearningActionMenuItem[];
};

export function FormateurElearningActionsMenu({
  menuKey,
  openMenuKey,
  onOpenMenuKeyChange,
  items,
}: Props) {
  const open = openMenuKey === menuKey;

  useEffect(() => {
    if (!open) return undefined;
    let remove: (() => void) | undefined;
    const timer = window.setTimeout(() => {
      const onDocClick = (ev: MouseEvent) => {
        const el = document.getElementById(`elearning-actions-${menuKey}`);
        if (el && !el.contains(ev.target as Node)) {
          onOpenMenuKeyChange(null);
        }
      };
      document.addEventListener('click', onDocClick);
      remove = () => document.removeEventListener('click', onDocClick);
    }, 0);
    return () => {
      clearTimeout(timer);
      remove?.();
    };
  }, [open, menuKey, onOpenMenuKeyChange]);

  return (
    <div id={`elearning-actions-${menuKey}`} className="relative shrink-0">
      <button
        type="button"
        className="rounded-md p-1.5 text-on-surface-variant transition-colors hover:bg-black/5"
        onClick={(e) => {
          e.stopPropagation();
          onOpenMenuKeyChange(open ? null : menuKey);
        }}
        aria-label="Actions"
        aria-expanded={open}
      >
        <LucideSymbolIcon name="more_vert" tone="inherit" size={20} />
      </button>
      {open ? (
        <ul
          className="absolute right-0 top-full z-50 mt-1 min-w-[190px] rounded-md border border-outline-variant/15 bg-surface-container-lowest py-1 text-left shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          {items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                disabled={item.disabled}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm disabled:opacity-50 ${
                  item.variant === 'danger'
                    ? 'text-error hover:bg-error/10'
                    : 'text-on-surface hover:bg-surface-container-high'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenMenuKeyChange(null);
                  item.onClick();
                }}
              >
                <LucideSymbolIcon name={item.icon} tone="inherit" size={18} />
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
